import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useRole } from "../lib/roleContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { KatlasModeChip, PolicyGatePassedChip } from "../components/Katlas";
import { formatGBP, relDate } from "../lib/format";
import { toast } from "sonner";
import {
  ChevronDown, ChevronUp, Filter, Inbox as InboxIcon, Check, X, Clock, AlertCircle,
  CheckCircle2, MinusCircle, MessageSquare, Eye, EyeOff, Sparkles, ShieldCheck, Handshake, Loader2
} from "lucide-react";

const STATUS_META = {
  needs_review: { label: "Needs review", color: "var(--rag-amber-text)", bg: "var(--rag-amber-bg)", Icon: Clock },
  blocked: { label: "Blocked", color: "var(--rag-red-text)", bg: "var(--rag-red-bg)", Icon: AlertCircle },
  approved: { label: "Approved", color: "var(--rag-green-text)", bg: "var(--rag-green-bg)", Icon: CheckCircle2 },
  declined: { label: "Declined", color: "#4F5052", bg: "#EEEEF1", Icon: MinusCircle },
  awaiting_info: { label: "Awaiting info", color: "#1F3A5F", bg: "#E5ECF5", Icon: MessageSquare },
};

const SEV_STYLES = {
  high: { color: "var(--rag-red-text)", bg: "var(--rag-red-bg)" },
  medium: { color: "var(--rag-amber-text)", bg: "var(--rag-amber-bg)" },
  low: { color: "#4F5052", bg: "#EEEEF1" },
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "needs_review", label: "Needs review", q: { status: "needs_review" } },
  { id: "high_alignment", label: "High alignment", q: { min_alignment: 80 } },
  { id: "blocked", label: "Blocked", q: { status: "blocked" } },
  { id: "credentials_incomplete", label: "Sponsor incomplete", q: { sponsor_credentials: "incomplete" } },
  { id: "credentials_pending", label: "Sponsor pending", q: { sponsor_credentials: "pending" } },
  { id: "pack_ready", label: "Driver sponsor-ready", q: { sponsor_ready: true } },
  { id: "awaiting_info", label: "Awaiting info", q: { status: "awaiting_info" } },
  { id: "approved", label: "Approved", q: { status: "approved" } },
];

export default function Inbox() {
  const { role } = useRole();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("needs_review");
  const [openId, setOpenId] = useState(null);

  const isOfficer = ["club_secretary", "duty_manager", "committee"].includes(role.id);

  const refresh = async () => {
    setLoading(true);
    const f = FILTERS.find((x) => x.id === filter) || {};
    const params = f.q || {};
    const d = await api.listProposals(params);
    setItems(d);
    setLoading(false);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const counts = useMemo(() => {
    const byStatus = {};
    items.forEach((i) => { byStatus[i.effective_status] = (byStatus[i.effective_status] || 0) + 1; });
    return byStatus;
  }, [items]);

  return (
    <div className="space-y-8" data-testid="inbox-page">
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="signal-bar" />
          <span className="tracking-eyebrow text-muted-foreground">Introduction review · Officer inbox</span>
        </div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold leading-tight">
          Promising matches, <span className="signal-text">ready for a human eye.</span>
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
          AI can suggest alignment. Club officers decide whether an introduction is appropriate.
          Each decision is receipted — and only authorised roles can approve.
        </p>
      </section>

      {!isOfficer && (
        <Card className="p-5 border-border race-card flex items-start gap-3" style={{ backgroundColor: "var(--rag-amber-bg)" }} data-testid="inbox-role-hint">
          <AlertCircle className="h-5 w-5 mt-0.5" style={{ color: "var(--rag-amber-text)" }} />
          <div className="text-sm">
            <div className="font-medium">You're viewing as <b>{role.label}</b>.</div>
            <div className="text-muted-foreground mt-0.5">Switch to <b>Club Secretary</b>, <b>Duty Manager</b> or <b>Committee</b> in the top bar to approve, decline or request information.</div>
          </div>
        </Card>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => { setFilter(f.id); setOpenId(null); }}
            data-testid={`filter-${f.id}`}
            className={`text-xs font-mono-ui px-2.5 py-1 rounded-full border transition-colors ${
              filter === f.id ? "bg-[#131318] text-white border-[#131318]" : "border-border hover:border-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <Card className="p-8 text-sm text-muted-foreground">Loading inbox…</Card>}

      {!loading && items.length === 0 && (
        <Card className="p-8 text-sm text-muted-foreground" data-testid="inbox-empty">No proposals match this filter.</Card>
      )}

      <div className="space-y-2">
        {items.map((it) => (
          <ProposalRow
            key={it.id}
            it={it}
            open={openId === it.id}
            onToggle={() => setOpenId(openId === it.id ? null : it.id)}
            onChanged={refresh}
            role={role}
            isOfficer={isOfficer}
          />
        ))}
      </div>

      <div className="text-xs text-muted-foreground border-t border-border pt-4 flex flex-wrap gap-3">
        <span>Totals in view:</span>
        {Object.entries(counts).map(([k, v]) => (
          <span key={k} className="font-mono-ui">· {STATUS_META[k]?.label || k}: {v}</span>
        ))}
      </div>
    </div>
  );
}

function ProposalRow({ it, open, onToggle, onChanged, role, isOfficer }) {
  const sm = STATUS_META[it.effective_status] || STATUS_META.needs_review;
  const a = it.live_alignment || {};
  const d = it.driver_brief || {};
  const s = it.sponsor_brief || {};
  const scoreColor = a.alignment_score >= 80 ? "var(--rag-green-text)" : a.alignment_score >= 60 ? "var(--rag-amber-text)" : "var(--rag-red-text)";

  return (
    <Card className="border-border race-card overflow-hidden" data-testid={`inbox-row-${it.id}`}>
      <button onClick={onToggle} className="w-full text-left p-4 sm:p-5 flex items-center gap-4 hover:bg-secondary/40 transition-colors" data-testid={`inbox-toggle-${it.id}`}>
        <div className="relative shrink-0">
          <img src={d.avatar_url} alt="" className="h-10 w-10 rounded-md object-cover bg-muted" />
          <span className="absolute -bottom-1 -right-1 h-5 min-w-5 px-1 rounded bg-[#F25E1D] text-white text-[10px] font-semibold grid place-items-center font-mono-ui">#{d.race_number}</span>
        </div>
        <div className="h-9 w-9 rounded-md grid place-items-center text-white font-semibold font-mono-ui text-xs shrink-0" style={{ backgroundColor: s.logo_color }}>{s.logo_initials}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold truncate">
            {d.name} <span className="text-muted-foreground font-normal">×</span> {s.name}
          </div>
          <div className="text-[11px] text-muted-foreground font-mono-ui mt-0.5">
            {d.racing_category} · {d.age_band} · gap {formatGBP(d.funding_gap)} · sponsor: {s.verified_status}
          </div>
        </div>
        <div className="hidden md:flex flex-col items-end">
          <div className="text-xs text-muted-foreground">Alignment</div>
          <div className="text-lg font-mono-ui font-semibold" style={{ color: scoreColor }}>{a.alignment_score}%</div>
        </div>
        <div className="hidden md:flex items-center gap-1">
          {(a.flags || []).slice(0, 3).map((f, i) => {
            const st = SEV_STYLES[f.severity] || SEV_STYLES.low;
            return <span key={i} className="text-[10px] font-mono-ui px-1.5 py-0.5 rounded" style={{ color: st.color, backgroundColor: st.bg }}>{f.severity}</span>;
          })}
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] tracking-eyebrow font-mono-ui px-2 py-1 rounded" style={{ color: sm.color, backgroundColor: sm.bg }}>
          <sm.Icon className="h-3 w-3" /> {sm.label}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <ProposalReview it={it} onChanged={onChanged} role={role} isOfficer={isOfficer} />
      )}
    </Card>
  );
}

function ProposalReview({ it, onChanged, role, isOfficer }) {
  const a = it.live_alignment || {};
  const d = it.driver_brief || {};
  const s = it.sponsor_brief || {};
  const nav = useNavigate();
  const [media, setMedia] = useState([]);
  const [shareIdx, setShareIdx] = useState(null); // null = all included
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(true);
  const [approveOpen, setApproveOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [question, setQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Single call: returns media + cached AI narrative on first view, instant thereafter
    api.getProposal(it.id).then((p) => {
      setMedia(p.driver_brief?.media || []);
      setAi(p.ai_narrative || null);
      setAiLoading(false);
    }).catch(() => setAiLoading(false));
    /* eslint-disable-next-line */
  }, [it.id]);

  const toggleShare = (i) => {
    setShareIdx((prev) => {
      const base = prev || new Set(media.map((_, idx) => idx));
      const next = new Set(base);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };
  const canApprove = isOfficer && a.ready_to_introduce && it.effective_status !== "approved" && it.effective_status !== "declined";

  const submitApprove = async () => {
    if (submitting) return;
    setSubmitting(true);
    const idxs = shareIdx ? Array.from(shareIdx) : media.map((_, i) => i);
    try {
      const m = await api.approveProposal(it.id, {
        reviewer_name: role.name, reviewer_role: role.label,
        ai_summary: ai, shareable_asset_indexes: idxs, custom_notes: notes,
      });
      toast.success("Introduction approved", { description: `Receipt ${m.id}` });
      setApproveOpen(false); setNotes("");
      nav(`/matches/${m.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setSubmitting(false);
    }
  };
  const submitDecline = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.declineProposal(it.id, { reviewer_name: role.name, reviewer_role: role.label, reason });
      toast.success("Introduction declined", { description: "Decision recorded in the evidence trail." });
      setDeclineOpen(false); setReason("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decline failed");
    } finally {
      setSubmitting(false);
    }
  };
  const submitInfo = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.requestProposalInfo(it.id, { reviewer_name: role.name, reviewer_role: role.label, question });
      toast.success("Info request recorded");
      setInfoOpen(false); setQuestion("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-t border-border p-5 sm:p-6 space-y-5 bg-[#FAFAFB]" data-testid={`inbox-review-${it.id}`}>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border p-4 bg-white">
          <div className="tracking-eyebrow text-muted-foreground">Driver evidence</div>
          <div className="text-sm mt-2">
            <b>{d.name}</b> · {d.series}<br />
            <span className="text-muted-foreground">{d.racing_category} · {d.age_band} · {d.location}</span><br />
            Funding gap: <b>{formatGBP(d.funding_gap)}</b><br />
            Sponsor readiness: <b style={{ color: d.sponsor_readiness?.ready_to_share ? "var(--rag-green-text)" : "var(--rag-amber-text)" }}>{d.sponsor_readiness?.score}%</b>
          </div>
        </div>
        <div className="rounded-lg border border-border p-4 bg-white">
          <div className="tracking-eyebrow text-muted-foreground">Sponsor credentials</div>
          <div className="text-sm mt-2">
            <b>{s.name}</b> · {s.type_label}<br />
            Verified: <b>{s.verified_status}</b><br />
            Aims: {(s.aims || []).join(", ")}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 tracking-eyebrow text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" /> AI alignment narrative
          </div>
          <span className="text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-0.5 rounded border border-border signal-bg signal-text">Advisory</span>
        </div>
        {aiLoading && <div className="text-sm text-muted-foreground mt-2">Drafting…</div>}
        {ai && <p className="text-[14px] mt-2 leading-relaxed">{ai.why_match}</p>}
        {ai?.caveats && <div className="text-[11px] text-muted-foreground mt-2 italic">Caveats: {ai.caveats.join(" · ")}</div>}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border p-4 bg-white">
          <div className="tracking-eyebrow text-muted-foreground">Alignment points</div>
          <ul className="mt-2 space-y-1 text-sm">
            {(a.alignment_points || []).map((p, i) => <li key={i} className="flex gap-2"><Check className="h-3.5 w-3.5 mt-0.5 text-[var(--rag-green-text)]" /><span>{p}</span></li>)}
            {!a.alignment_points?.length && <li className="text-muted-foreground text-sm">None automatic.</li>}
          </ul>
        </div>
        <div className="rounded-lg border border-border p-4 bg-white">
          <div className="tracking-eyebrow text-muted-foreground">Gaps &amp; risks</div>
          <ul className="mt-2 space-y-1.5 text-sm">
            {(a.flags || []).length === 0 && <li className="text-muted-foreground text-sm">No automatic flags.</li>}
            {(a.flags || []).map((f, i) => {
              const st = SEV_STYLES[f.severity] || SEV_STYLES.low;
              return (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-0.5 rounded shrink-0 mt-0.5" style={{ color: st.color, backgroundColor: st.bg }}>{f.severity}</span>
                  <span>{f.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {media.length > 0 && (
        <div className="rounded-lg border border-border p-4 bg-white">
          <div className="flex items-center justify-between">
            <div className="tracking-eyebrow text-muted-foreground">What can be shared</div>
            <span className="text-[11px] text-muted-foreground">Untick anything you'd withhold for this introduction.</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mt-3">
            {media.map((m, i) => {
              const checked = shareIdx === null ? true : shareIdx.has(i);
              return (
                <label key={i} className="flex items-center gap-3 rounded-md border border-border p-2.5 cursor-pointer" data-testid={`inbox-share-${it.id}-${i}`}>
                  <Checkbox checked={checked} onCheckedChange={() => toggleShare(i)} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{m.title}</div>
                    <div className="text-[10px] tracking-eyebrow font-mono-ui text-muted-foreground">{m.badge}</div>
                  </div>
                  {checked ? <Eye className="h-3.5 w-3.5 text-[var(--rag-green-text)]" /> : <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Past info requests / decline reason */}
      {it.info_requests && it.info_requests.length > 0 && (
        <div className="rounded-lg border border-border p-4 bg-white">
          <div className="tracking-eyebrow text-muted-foreground">Information requested</div>
          <ul className="mt-2 space-y-1.5 text-sm">
            {it.info_requests.map((q) => (
              <li key={q.id} className="font-mono-ui text-xs">
                <span className="text-muted-foreground">{relDate(q.timestamp)}</span> — <b className="font-sans">{q.asked_by}</b> ({q.asked_role}): {q.question}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-2">
        <Button onClick={() => setApproveOpen(true)} disabled={!canApprove} data-testid={`approve-${it.id}`}>
          <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve introduction
        </Button>
        <Button variant="outline" onClick={() => setDeclineOpen(true)} disabled={!isOfficer || ["approved", "declined"].includes(it.effective_status)} data-testid={`decline-${it.id}`}>
          <MinusCircle className="h-4 w-4 mr-1.5" /> Decline
        </Button>
        <Button variant="outline" onClick={() => setInfoOpen(true)} disabled={!isOfficer || ["approved", "declined"].includes(it.effective_status)} data-testid={`request-info-${it.id}`}>
          <MessageSquare className="h-4 w-4 mr-1.5" /> Request information
        </Button>
        {it.effective_status === "approved" && (
          <Link to={`/matches/${it.id}`} className="ml-auto text-sm underline-offset-4 hover:underline" data-testid={`open-receipt-${it.id}`}>
            Open introduction receipt →
          </Link>
        )}
        {!a.ready_to_introduce && !["approved", "declined"].includes(it.effective_status) && (
          <span className="text-xs text-muted-foreground">High-severity gaps must clear first.</span>
        )}
      </div>

      {/* Dialogs */}
      <Dialog open={approveOpen} onOpenChange={(o) => !submitting && setApproveOpen(o)}>
        <DialogContent data-testid={`approve-dialog-${it.id}`}>
          <DialogHeader>
            <DialogTitle>Approve introduction</DialogTitle>
            <DialogDescription>Approving as <b>{role.name}</b> ({role.label}). A read-only match receipt will be created and can be shared.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5">
            <KatlasModeChip />
            <PolicyGatePassedChip passed />
          </div>
          <Textarea placeholder="Optional conditions (scope, expiry, expectations)…" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`approve-notes-${it.id}`} disabled={submitting} />
          {submitting && (
            <div
              className="flex items-center gap-2 rounded-md border border-border bg-[#FFF7F2] px-3 py-2 text-[12px] text-muted-foreground"
              data-testid={`approve-saving-banner-${it.id}`}
              role="status"
              aria-live="polite"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[#F25E1D]" />
              Saving the receipt &amp; share token… this can take 10–15 seconds.
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveOpen(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submitApprove} disabled={submitting} data-testid={`approve-confirm-${it.id}`}>
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</>
              ) : "Approve & create receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent data-testid={`decline-dialog-${it.id}`}>
          <DialogHeader>
            <DialogTitle>Decline introduction</DialogTitle>
            <DialogDescription>Recorded as a decision by <b>{role.name}</b> ({role.label}).</DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Reason for declining (visible in evidence trail)…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`decline-reason-${it.id}`} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeclineOpen(false)}>Cancel</Button>
            <Button onClick={submitDecline} disabled={!reason.trim() || submitting} data-testid={`decline-confirm-${it.id}`}>
              {submitting ? "Saving…" : "Decline"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent data-testid={`info-dialog-${it.id}`}>
          <DialogHeader>
            <DialogTitle>Request more information</DialogTitle>
            <DialogDescription>Status will move to <b>Awaiting info</b>. The question is logged on the proposal.</DialogDescription>
          </DialogHeader>
          <Textarea placeholder="What do you need from the sponsor or coach before approving?" value={question} onChange={(e) => setQuestion(e.target.value)} data-testid={`info-question-${it.id}`} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInfoOpen(false)}>Cancel</Button>
            <Button onClick={submitInfo} disabled={!question.trim() || submitting} data-testid={`info-confirm-${it.id}`}>
              {submitting ? "Saving…" : "Record request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
